import { create } from 'zustand';
import { isTokenExpired, learnClockSkew } from '@/lib/jwt';

/**
 * Auth store — reads/writes the SAME localStorage keys (`ukn_token`,
 * `ukn_user`) used by the legacy HTML pages, so a user logged in via
 * `/employees.html` is automatically logged in on `/app/*` and vice versa.
 *
 * Token persistence is direct localStorage (no Zustand persist middleware)
 * to keep the contract bit-identical to assets/js/auth.js.
 *
 * Expiry, cross-tab sync and the "server rejected this session" path live in
 * lib/session.ts; this file only holds the state.
 */

export const TOKEN_KEY = 'ukn_token';
export const USER_KEY  = 'ukn_user';

export type UknUser = {
  id: string;
  username: string;
  full_name?: string | null;
  email?: string | null;
  role: string;
  employee_id?: string | null;
  /** SIGNUP: standalone workspace (no uKnowva behind it). HRMS / null otherwise. */
  workspace_origin?: string | null;
};

/** Why the last session ended — the login page tells the user. */
export type SessionEndReason = 'expired' | 'signed-out' | 'signed-out-elsewhere';

type AuthState = {
  token: string | null;
  user: UknUser | null;
  isAuthenticated: boolean;
  /** Set when a session ends, cleared on the next sign-in. */
  endReason: SessionEndReason | null;
  setSession: (token: string, user: UknUser) => void;
  clear: (reason?: SessionEndReason) => void;
};

/** The session as localStorage holds it right now — possibly written by
 *  another tab, or by a legacy HTML page. */
export function readStoredSession(): { token: string | null; user: UknUser | null } {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(USER_KEY);
    return { token, user: raw ? (JSON.parse(raw) as UknUser) : null };
  } catch {
    return { token: null, user: null };
  }
}

function removeStoredSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
}

/** Boot state. A token that ran out while the tab (or browser) was closed is
 *  dropped here, so the app opens on the login page instead of rendering the
 *  shell and letting every query on the first screen fail. */
function initialState(): Pick<AuthState, 'token' | 'user' | 'isAuthenticated' | 'endReason'> {
  const { token, user } = readStoredSession();
  if (token && isTokenExpired(token)) {
    removeStoredSession();
    return { token: null, user: null, isAuthenticated: false, endReason: 'expired' };
  }
  return { token, user, isAuthenticated: !!token, endReason: null };
}

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState(),

  setSession(token, user) {
    learnClockSkew(token);
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch { /* ignore quota errors */ }
    set({ token, user, isAuthenticated: true, endReason: null });
  },

  clear(reason = 'signed-out') {
    removeStoredSession();
    set({ token: null, user: null, isAuthenticated: false, endReason: reason });
  },
}));
